import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ErrorNotification from './components/ErrorNotification';
import ProgressBar from './components/ProgressBar';
import UploadZone from './components/UploadZone';
import PreviewGallery from './components/PreviewGallery';
import AnalysisOptionsDialog from './components/AnalysisOptionsDialog';

import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { analyzePhotosWithGemini } from '../../services/photoAnalysisService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { compressToThumbnail, uploadThumbnail } from '../../utils/thumbnailCompression';
import BottomNav from '../../components/navigation/BottomNav';
import { isAuthError } from '../../utils/isAuthError';
import Header from '../../components/navigation/Header';

const PhotoUploadInterface = () => {
  const navigate = useNavigate();
  const { user, userProfile, creditBalance, refreshCredits, refreshAnalysisHistory, isAuthenticated, signOut } = useAuth();

  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [gender, setGender] = useState('male');
  const [analysisError, setAnalysisError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [showAnalysisOptions, setShowAnalysisOptions] = useState(false);

  const maxFiles = 9;
  const SUPPORTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const validateFile = useCallback((file) => {
    if (!SUPPORTED_FORMATS?.includes(file?.type)) {
      return {
        valid: false,
        error: 'Unsupported format',
        title: 'Invalid File Format',
        message: `${file?.name} is not a supported format.`,
        suggestion: 'Please use JPG, PNG, or HEIC files only.'
      };
    }

    if (file?.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: 'File too large',
        title: 'File Size Exceeded',
        message: `${file?.name} exceeds the 10MB limit.`,
        suggestion: 'Try compressing the image or use a smaller file.'
      };
    }

    if (file?.size === 0) {
      return {
        valid: false,
        error: 'Empty file',
        title: 'Invalid File',
        message: `${file?.name} appears to be empty or corrupted.`,
        suggestion: 'Please select a valid image file.'
      };
    }

    return { valid: true };
  }, []);

  const handleFilesSelected = useCallback((newFiles) => {
    const remainingSlots = maxFiles - files?.length;
    
    if (remainingSlots <= 0) {
      setErrors([{
        title: 'Upload Limit Reached',
        message: `You can only upload ${maxFiles} photos at a time.`,
        suggestion: 'Remove some photos to add new ones.'
      }]);
      return;
    }

    if (newFiles?.length === 0) {
      setErrors([{
        title: 'No Files Selected',
        message: 'No files were selected for upload.',
        suggestion: 'Please select at least one image file.'
      }]);
      return;
    }

    const filesToAdd = newFiles?.slice(0, remainingSlots);
    const newErrors = [];

    const processedFiles = filesToAdd?.map((file, index) => {
      const validation = validateFile(file);
      const fileId = `${Date.now()}-${index}`;

      if (!validation?.valid) {
        newErrors?.push(validation);
      }

      return {
        id: fileId,
        file,
        name: file?.name,
        size: file?.size,
        type: file?.type,
        preview: URL.createObjectURL(file),
        alt: `User uploaded photo ${files?.length + index + 1} named ${file?.name}`,
        valid: validation?.valid,
        error: validation?.error || null
      };
    });

    setFiles(prev => [...prev, ...processedFiles]);
    
    if (newErrors?.length > 0) {
      setErrors(prev => [...prev, ...newErrors]);
    }

    if (newFiles?.length > remainingSlots) {
      setErrors(prev => [...prev, {
        title: 'Some Files Not Added',
        message: `Only ${remainingSlots} slot${remainingSlots !== 1 ? 's' : ''} available. ${newFiles?.length - remainingSlots} file${newFiles?.length - remainingSlots !== 1 ? 's' : ''} not added.`,
        suggestion: 'Remove existing photos to add more.'
      }]);
    }
  }, [files?.length, maxFiles, validateFile]);

  const handleRemoveFile = useCallback((fileId) => {
    setFiles(prev => {
      const fileToRemove = prev?.find(f => f?.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove?.preview);
      }
      return prev?.filter(f => f?.id !== fileId);
    });
  }, []);

  const handleClearAllPhotos = useCallback(() => {
    setShowClearConfirm(true);
  }, []);

  const confirmClearAllPhotos = useCallback(() => {
    files?.forEach(file => {
      if (file?.preview) {
        URL.revokeObjectURL(file?.preview);
      }
    });
    setFiles([]);
    setErrors([]);
    setAnalysisError(null);
  }, [files]);

  const handleDismissError = useCallback((index) => {
    setErrors(prev => prev?.filter((_, i) => i !== index));
  }, []);

  const handleStartRanking = useCallback(async () => {
    const validFiles = files?.filter(f => f?.valid);
    
    if (validFiles?.length === 0) {
      setErrors([{
        title: 'No Valid Photos',
        message: 'Please add at least one valid photo to start ranking.',
        suggestion: 'Make sure your photos are in JPG, PNG, or HEIC format and under 10MB.'
      }]);
      return;
    }

    if (!isAuthenticated) {
      setShowAnalysisOptions(true);
      return;
    }

    if (creditBalance <= 0) {
      setShowCreditWarning(true);
      return;
    }

    setShowStartConfirm(true);
  }, [files, creditBalance, isAuthenticated]);

  const confirmStartRanking = useCallback(async () => {
    const validFiles = files?.filter(f => f?.valid);
    
    if (!isAuthenticated) {
      setShowAnalysisOptions(true);
      return;
    }

    if (creditBalance <= 0) {
      setShowCreditWarning(true);
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);
    setAnalysisError(null);
    setErrors([]);
    
    const startTime = Date.now();
    
    try {
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          const increment = Math.floor(Math.random() * 10) + 5;
          return Math.min(prev + increment, 95);
        });
      }, 800);

      const analysisResult = await analyzePhotosWithGemini(validFiles, gender);
      
      let enrichedResult = null;

      try {
        const newBalance = creditBalance - 1;
        const { error: creditError } = await supabase
          ?.from('credits')
          ?.update({ balance: newBalance })
          ?.eq('user_id', user?.id);

        if (creditError) {
          clearInterval(progressInterval);
          setIsProcessing(false);
          setProcessingProgress(0);
          if (isAuthError(creditError)) {
            setAnalysisError('Your session has expired. Please sign in again to complete the analysis.');
            setShowAnalysisOptions(true);
          } else {
            setAnalysisError(`Credit deduction failed: ${creditError?.message || 'Unknown error'}. Your analysis was not saved. Please try again.`);
          }
          return;
        }

        const rankedPhotos = analysisResult?.rankedPhotos || [];
        const scores = rankedPhotos?.map(p => p?.overallScore)?.filter(Boolean);
        const avgScore = scores?.length > 0 ? scores?.reduce((a, b) => a + b, 0) / scores?.length : null;
        const highestScore = scores?.length > 0 ? Math.max(...scores) : null;

        const sessionId = `${Date.now()}-${Math.random()?.toString(36)?.slice(2, 9)}`;

        const top3Photos = rankedPhotos?.slice(0, 3);
        const thumbnailPaths = [];

        for (let i = 0; i < top3Photos?.length; i++) {
          try {
            const originalFile = validFiles?.[i]?.file || validFiles?.[i];
            if (originalFile instanceof File) {
              const thumbnailBlob = await compressToThumbnail(originalFile);
              const storagePath = await uploadThumbnail(supabase, thumbnailBlob, user?.id, sessionId, i + 1);
              thumbnailPaths?.push(storagePath);
            }
          } catch (thumbErr) {
            console.warn(`Thumbnail ${i + 1} upload failed, skipping:`, thumbErr?.message);
            thumbnailPaths?.push(null);
          }
        }

        const enrichedRankedPhotos = rankedPhotos?.map((photo, idx) => ({
          ...photo,
          thumbnailPath: thumbnailPaths?.[idx] || null
        }));

        enrichedResult = {
          ...analysisResult,
          rankedPhotos: enrichedRankedPhotos
        };

        const { error: historyError } = await supabase?.from('analysis_history')?.insert({
          user_id: user?.id,
          photo_count: validFiles?.length,
          average_score: avgScore ? parseFloat(avgScore?.toFixed(2)) : null,
          highest_score: highestScore ? parseFloat(highestScore?.toFixed(2)) : null,
          gender_filter: gender,
          credits_used: 1,
          results: enrichedResult
        });

        if (historyError) {
          clearInterval(progressInterval);
          setIsProcessing(false);
          setProcessingProgress(0);
          if (isAuthError(historyError)) {
            setAnalysisError('Your session expired while saving results. Please sign in again — your credit has been used.');
            setShowAnalysisOptions(true);
          } else {
            setAnalysisError(`Results could not be saved: ${historyError?.message || 'Unknown error'}. Please try again.`);
          }
          return;
        }

        await refreshCredits();
        await refreshAnalysisHistory();
      } catch (creditError) {
        console.error('Credit deduction error:', creditError);
        clearInterval(progressInterval);
        setIsProcessing(false);
        setProcessingProgress(0);
        setAnalysisError('An unexpected error occurred while saving your results. Please try again.');
        return;
      }
      
      clearInterval(progressInterval);
      setProcessingProgress(100);
      
      setTimeout(() => {
        navigate('/ranking-results-display', {
          state: {
            analysisResult: enrichedResult,
            gender,
            uploadedFiles: validFiles
          }
        });
      }, 1000);
    } catch (error) {
      console.error('Analysis failed:', error);
      const errorTimestamp = Date.now();
      const duration = errorTimestamp - startTime;
      
      let errorDetails = {
        message: error?.message || 'Unable to analyze photos at this time.',
        timestamp: errorTimestamp,
        duration,
        type: 'general',
        canRetry: true
      };

      const errorMessage = error?.message?.toLowerCase() || '';
      if (errorMessage?.includes('network') || errorMessage?.includes('fetch')) {
        errorDetails.type = 'network';
      } else if (errorMessage?.includes('timeout')) {
        errorDetails.type = 'timeout';
      } else if (errorMessage?.includes('401') || errorMessage?.includes('403')) {
        errorDetails.type = 'auth';
        errorDetails.canRetry = false;
      } else if (errorMessage?.includes('429')) {
        errorDetails.type = 'rate_limit';
      }

      setAnalysisError(errorDetails);
      setRetryCount(prev => prev + 1);
      
      setErrors([{
        title: 'Analysis Failed',
        message: error?.message || 'Unable to analyze photos. This could be due to network issues or AI service unavailability.',
        suggestion: 'Check the recovery guidance below for step-by-step instructions.'
      }]);
      
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  }, [files, gender, navigate, creditBalance, isAuthenticated, user, refreshCredits]);

  const handleTrialAnalysis = useCallback(async () => {
    setShowAnalysisOptions(false);
    const validFiles = files?.filter(f => f?.valid)?.slice(0, 3);

    setIsProcessing(true);
    setProcessingProgress(0);
    setAnalysisError(null);
    setErrors([]);

    try {
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev >= 95) { clearInterval(progressInterval); return prev; }
          return Math.min(prev + Math.floor(Math.random() * 10) + 5, 95);
        });
      }, 800);

      const analysisResult = await analyzePhotosWithGemini(validFiles, gender);

      clearInterval(progressInterval);
      setProcessingProgress(100);

      setTimeout(() => {
        navigate('/ranking-results-display', {
          state: {
            analysisResult,
            gender,
            uploadedFiles: validFiles,
            isTrial: true
          }
        });
      }, 1000);
    } catch (error) {
      console.error('Trial analysis failed:', error);
      setErrors([{
        title: 'Analysis Failed',
        message: error?.message || 'Unable to analyze photos. Please try again.',
        suggestion: 'Check your connection and try again.'
      }]);
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  }, [files, gender, navigate]);

  const validFiles = files?.filter(f => f?.valid);
  const canStartRanking = validFiles?.length > 0 && !isProcessing;

  return (
    <div className="min-h-screen bg-white pb-20">
      <Header
        user={user}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={signOut}
      />
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 pb-8 pt-12">
        {/* Decorative Elements */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            rotate: [0, 5, 0]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-8 text-pink-300 opacity-40"
        >
          <Icon name="Heart" size={48} />
        </motion.div>
        
        <motion.div
          animate={{ 
            y: [0, 15, 0],
            rotate: [0, -5, 0]
          }}
          transition={{ 
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
          className="absolute top-32 right-8 text-cyan-300 opacity-40"
        >
          <Icon name="Star" size={40} />
        </motion.div>

        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 10, 0]
          }}
          transition={{ 
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-12 left-12 text-purple-300 opacity-30"
        >
          <Icon name="Sparkles" size={36} />
        </motion.div>

        <div className="container mx-auto px-4 max-w-4xl text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Find Your Best{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
              Angle!
            </span>
            <span className="inline-block ml-2 text-cyan-400">
              <Icon name="Sparkles" size={36} />
            </span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-gray-600 text-lg mb-8"
          >
            AI-powered ranking to help you find love faster.
          </motion.p>

          {/* Sample Ranking Preview */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex items-center justify-center gap-4 mb-8 px-4"
          >
            {/* #2 Photo */}
            <div className="relative transform -rotate-6">
              <div className="w-28 h-36 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl shadow-lg overflow-hidden border-4 border-white">
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <Icon name="User" size={48} />
                </div>
              </div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-full shadow-md border-2 border-gray-200">
                <span className="text-sm font-bold text-gray-700">#2</span>
              </div>
            </div>

            {/* #1 Photo - Center with Crown */}
            <div className="relative z-10">
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-yellow-500"
              >
                <Icon name="Crown" size={32} />
              </motion.div>
              <div className="w-36 h-48 bg-gradient-to-br from-purple-400 to-blue-500 rounded-3xl shadow-2xl overflow-hidden border-4 border-white">
                <div className="w-full h-full flex items-center justify-center text-white">
                  <Icon name="User" size={64} />
                </div>
              </div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-orange-500 px-4 py-1.5 rounded-full shadow-lg">
                <span className="text-sm font-bold text-white">TOP PICK</span>
              </div>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-4 right-2 text-pink-400"
              >
                <Icon name="Heart" size={20} />
              </motion.div>
            </div>

            {/* #3 Photo */}
            <div className="relative transform rotate-6">
              <div className="w-28 h-36 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl shadow-lg overflow-hidden border-4 border-white">
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <Icon name="User" size={48} />
                </div>
              </div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-full shadow-md border-2 border-gray-200">
                <span className="text-sm font-bold text-gray-700">#3</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      {/* Main Content */}
      <div className="container mx-auto px-4 max-w-4xl py-6">
        {/* Gender Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Use filters for
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => setGender('male')}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                gender === 'male' ?'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Men
            </button>
            <button
              onClick={() => setGender('female')}
              className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                gender === 'female' ?'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Women
            </button>
          </div>
        </div>

        {/* Error Notifications */}
        {errors?.length > 0 && (
          <div className="mb-6 space-y-3">
            <ErrorNotification
              errors={errors}
              onDismiss={handleDismissError}
            />
          </div>
        )}

        {/* Processing Progress */}
        {isProcessing && (
          <div className="mb-6">
            <ProgressBar 
              progress={processingProgress} 
              isComplete={processingProgress === 100}
            />
          </div>
        )}

        {/* Upload Zone */}
        {files?.length === 0 && (
          <div className="mb-6">
            <UploadZone
              onFilesSelected={handleFilesSelected}
              disabled={isProcessing}
              maxFiles={maxFiles}
              currentFileCount={files?.length}
            />
          </div>
        )}

        {/* Preview Gallery - Your Lineup */}
        {files?.length > 0 && (
          <div className="mb-6">
            <PreviewGallery
              files={files}
              onRemove={handleRemoveFile}
              onClearAll={handleClearAllPhotos}
              maxFiles={maxFiles}
              onAddMore={handleFilesSelected}
              creditBalance={creditBalance}
            />
          </div>
        )}

        {/* Premium Unlock CTA Card */}
        {files?.length >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate('/purchase-credits')}
            className="mb-6 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl text-center cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            <div className="flex flex-col items-center">
              <Icon name="Crown" size={32} className="text-yellow-400 mb-3" />
              <h3 className="text-lg font-bold mb-1">
                Purchase credits and unlock all features
              </h3>
              <p className="text-sm text-gray-300">
                See why you ranked lower on #3
              </p>
            </div>
          </motion.div>
        )}

        {/* Main CTA Button */}
        {validFiles?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Button
              onClick={handleStartRanking}
              disabled={!canStartRanking}
              className="w-full py-4 text-lg font-semibold rounded-2xl bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Analyze & Rank Photos 🚀
            </Button>
          </motion.div>
        )}
      </div>
      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showStartConfirm}
        onClose={() => setShowStartConfirm(false)}
        onConfirm={() => {
          setShowStartConfirm(false);
          confirmStartRanking();
        }}
        title="Start Photo Ranking?"
        message={`This will analyze ${validFiles?.length} photo${validFiles?.length !== 1 ? 's' : ''} and use 1 credit. You have ${creditBalance} credit${creditBalance !== 1 ? 's' : ''} remaining.`}
        confirmText="Start Analysis"
        cancelText="Cancel"
      />
      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={() => {
          setShowClearConfirm(false);
          confirmClearAllPhotos();
        }}
        title="Clear All Photos?"
        message="This will remove all uploaded photos. This action cannot be undone."
        confirmText="Clear All"
        cancelText="Keep Photos"
        variant="destructive"
      />
      <AnalysisOptionsDialog
        isOpen={showAnalysisOptions}
        onClose={() => setShowAnalysisOptions(false)}
        onSignIn={() => {
          setShowAnalysisOptions(false);
          navigate('/auth', { state: { from: { pathname: '/photo-upload-interface' } } });
        }}
        onTrial={handleTrialAnalysis}
        photoCount={validFiles?.length}
      />
      <ConfirmDialog
        isOpen={showCreditWarning}
        onClose={() => setShowCreditWarning(false)}
        onConfirm={() => {
          setShowCreditWarning(false);
          navigate('/purchase-credits');
        }}
        title={!isAuthenticated ? 'Sign In Required' : 'No Credits Remaining'}
        message={!isAuthenticated ? 'Please sign in to analyze photos and track your credits.' : 'You need credits to analyze photos. Purchase more credits to continue.'}
        confirmText={!isAuthenticated ? 'Sign In' : 'Buy Credits'}
        cancelText="Cancel"
      />
      {/* Hidden camera input */}
      <input
        id="camera-file-input-upload"
        type="file"
        capture="environment"
        className="hidden"
        accept="image/jpeg,image/png,image/heic,image/jpg"
        onChange={(e) => handleFilesSelected(Array.from(e?.target?.files || []))}
      />
      <BottomNav activePath="/photo-upload-interface" cameraInputId="camera-file-input-upload" />
    </div>
  );
};

export default PhotoUploadInterface;